import type {OptionFilterItem} from '@/modules/common/models/filters';

import {useBreakpointDown, Text} from '@travel-frontend/core';
import {Flex, Radio} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {Label} from '@/components/FilterCategoryItems/components/Label';

type Props = {
  items: Array<OptionFilterItem>;
  onChange: (param: {type: 'clear' | 'set'; value?: null | number}) => void;
  canShowOptionBestPrice?: boolean;
};

export const FilterCategoryItems: React.FunctionComponent<Props> = ({
  items,
  onChange,
  canShowOptionBestPrice,
}) => {
  const {t} = useTranslation(['general', 'filters']);
  const mdDown = useBreakpointDown('md');

  const handleChange = (value: OptionFilterItem['value']) => () => {
    if (value) {
      onChange({type: 'set', value: Number(value)});
    } else {
      onChange({type: 'clear', value: null});
    }
  };

  return (
    <Flex spacing={0}>
      {items.map((item) => {
        const isAvailable = (item?.count ?? 0) > 0;
        return (
          <Radio
            size={mdDown ? 'medium' : 'small'}
            label={
              <Flex
                flex={1}
                justifyContent="space-between"
                alignItems="center"
                direction="row"
              >
                <Label checked={item.active}>
                  {item.value
                    ? t('filters:category.label', {
                        count: Number(item.value),
                      })
                    : t('filters:category.default')}
                </Label>
                {canShowOptionBestPrice && (
                  <Text
                    color="contentSecondary"
                    preset={mdDown ? 'labelSmall' : 'labelXSmall'}
                  >
                    {t('general:bestPrice', {
                      price: item.priceMin?.amount,
                      currency: import.meta.env.VITE_PORTAL_CURRENCY,
                    })}
                  </Text>
                )}
              </Flex>
            }
            key={item.value}
            checked={item.active}
            disabled={!isAvailable}
            data-testid={item.active ? 'item-selected' : 'item'}
            onChange={isAvailable ? handleChange(item.value) : undefined}
          />
        );
      })}
    </Flex>
  );
};
